import { InputGroup } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React from 'react';
import { TLCode } from '../../../common/enums';
import { LocaleStore } from '../../../store';
import { Municipality } from '../../../store/models/Municipality';
import { MunicipalityStore } from '../../../store/MunicipalityStore';
import BaseGrid from '../../common/grid/BaseGrid';
import EditCell from '../../common/grid/EditCell';
import withTranslate, { WithTranslateProps } from '../../common/WithTranslate';
import { MunicipalityDialogForm } from './MunicipalityDialogForm';

interface MunicipalityListProps extends WithTranslateProps {
  MunicipalityStore?: Instance<typeof MunicipalityStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
}

interface MunicipalityListState {
  isLoading: boolean;
  municipalityToEdit?: Instance<typeof Municipality>;
  filter: string;
}

interface MunicipalityToDisplay {
  id: number;
  ins6Code: string;
  entityNameFr: string;
  postalCode: string;
  ins5Code: string;
  mainMunicipalityFr: string;
  population: number;
}

@inject('MunicipalityStore', 'LocaleStore')
@observer
class MunicipalityList extends React.Component<MunicipalityListProps, MunicipalityListState> {
  state = {
    isLoading: false,
    municipalityToEdit: undefined,
    filter: '',
  } as MunicipalityListState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { MunicipalityStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    await MunicipalityStore!.checkListLoaded(); // loadList();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onEditClose = () => {
    this.setState({ municipalityToEdit: undefined });
  };

  onSaveMunicipality = (municipality: any) => {
    this.props.MunicipalityStore!.saveMunicipality(municipality);
    this.onEditClose();
  };

  setMunicipalityToEdit = (id: any) => {
    const municipalityToEdit = this.props.MunicipalityStore!.dataList.find((m) => m.municipalityId === id);
    this.setState({ municipalityToEdit: municipalityToEdit });
  };

  columnsDefinition = [
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_Id),
      width: 60,
      sort: 'asc',
      field: 'id',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_INS6Code),
      width: 80,
      sort: 'asc',
      field: 'ins6Code',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_EntityNameFr),
      width: 150,
      sort: 'asc',
      field: 'entityNameFr',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_PostalCode),
      width: 60,
      sort: 'asc',
      field: 'postalCode',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_Ins5Code),
      width: 80,
      sort: 'asc',
      field: 'ins5Code',
    },

    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_MainMunicipalityFr),
      width: 150,
      sort: 'asc',
      field: 'mainMunicipalityFr',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_Population),
      width: 80,
      sort: 'asc',
      field: 'population',
    },
    {
      headerName: '',
      width: 20,
      cellRendererFramework: EditCell,
      cellRendererParams: (params: ICellRendererParams) => {
        return {
          onEditButtonClick: () => {
            this.setMunicipalityToEdit(params.data.id);
          },
        };
      },
    },
  ] as (ColDef | ColGroupDef)[];

  buildGridData = () => {
    return this.props.MunicipalityStore!.dataList.map((m) => {
      return {
        id: m.municipalityId,
        entityNameFr: m.nameFR,
        postalCode: m.postalCode.toString(),
        population: m.population,
        mainMunicipalityFr: m.mainNameFR,
        ins5Code: m.mainINSCode,
        ins6Code: m.insCode,
      } as MunicipalityToDisplay;
    });
  };

  render() {
    return (
      <Container>
        <MunicipalityDialogForm
          municipality={this.state.municipalityToEdit}
          onClose={this.onEditClose}
          onSave={this.onSaveMunicipality}
        />
        <DivLine>
          <DivLeft>
            <InputGroup
              placeholder="Filter"
              leftIcon="search"
              value={this.state.filter}
              onChange={(e) => this.setState({ filter: e.target.value })}
            />
          </DivLeft>
        </DivLine>
        <BaseGrid
          keyName="id"
          defaultColDef={{
            filter: false,
            sortable: true,
          }}
          columnDefs={this.columnsDefinition}
          rowData={this.buildGridData()}
          loading={this.state.isLoading}
          quickFilterText={this.state.filter}
          onRowDoubleClicked={(ev) => {
            this.setMunicipalityToEdit(ev.data.id);
          }}
        />
      </Container>
    );
  }
}

export default withTranslate(MunicipalityList);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
